import React from 'react';
import clsx from 'clsx';
import { Search } from 'lucide-react';
import Badge from './Badge.jsx';
import { Button } from './Button.jsx';

function renderFilterSlot(filter, index) {
  if (React.isValidElement(filter)) {
    return <div key={`filter-slot-${index}`}>{filter}</div>;
  }

  if (!filter || typeof filter !== 'object') {
    return null;
  }

  const key = filter.id || `filter-slot-${index}`;

  return (
    <div key={key} className="min-w-[170px] space-y-1">
      {filter.label ? <p className="text-xs font-medium text-gray-600">{filter.label}</p> : null}
      {filter.control}
    </div>
  );
}

export default function FilterToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
  actions = [],
  activeFilterCount = 0,
  onReset,
  children,
  className,
  compact = false,
}) {
  const showSearch = typeof onSearchChange === 'function';

  return (
    <div
      className={clsx(
        'rounded-lg border border-gray-200 bg-white',
        compact ? 'p-3' : 'p-4',
        className
      )}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-1 flex-wrap items-end gap-3">
          {showSearch ? (
            <div className={clsx('space-y-1', compact ? 'min-w-[220px]' : 'min-w-[260px]')}>
              <p className="text-xs font-medium text-gray-600">Search</p>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={searchValue || ''}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder={searchPlaceholder}
                  className="h-10 w-full rounded-md border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          ) : null}

          {filters.map((filter, index) => renderFilterSlot(filter, index))}
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          {activeFilterCount > 0 ? (
            <Badge variant="outline-default" size="sm">
              {activeFilterCount} active
            </Badge>
          ) : null}

          {typeof onReset === 'function' ? (
            <Button type="button" size="sm" variant="secondary" onClick={onReset}>
              Reset
            </Button>
          ) : null}

          {actions.map((action, index) => (
            <React.Fragment key={`toolbar-action-${index}`}>{action}</React.Fragment>
          ))}
        </div>
      </div>

      {children ? <div className="mt-3 flex flex-wrap items-center gap-2">{children}</div> : null}
    </div>
  );
}
